import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { MessageSchema, UnitPrecisionSchema } from './primitives.js';
import { SETTINGS_REGISTRY } from './org-settings.js';
const c = initContract();
/**
 * Stock counts (INV-05 §5.7 — the stock take): counting what is really on
 * the shelf and correcting the book to match.
 *
 * Open = freely editable — counts typed, retyped, cleared — and NOTHING
 * moves (BR-INV-05-18, R8). Posting re-reads the live balances under lock,
 * writes one Stock Take movement per DISCREPANT line at the location's
 * current cost, posts the write-off/found entry (INV-05 §12), stamps the
 * REAL user and time (BR-INV-05-16), and locks the count.
 *
 * THE SIGN RULE (BR-INV-05-12, labelled so nobody misreads it):
 *   variance = system − counted. A POSITIVE variance means stock is MISSING.
 * A blank counted quantity means "not counted", never zero (BR-INV-05-13).
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const COUNT_MESSAGES = {
    /**
     * BR-INV-05-12's on-screen label, verbatim — and ERR-INV-05-06, which is the
     * same sentence arrived at from the other side: a reader took `+2` for two
     * EXTRA reams, so the label has to say which way the subtraction runs.
     */
    varianceLabel: 'Variance = system − counted. A positive number means stock is missing.',
    /** BR-INV-05-14 / ERR-INV-05-05 — the client confirm before switching warehouse mid-count. */
    switchWarehouse: (count) => `Switching warehouse will discard the ${count} counts you have entered. Continue?`,
    /** Posting with nothing counted. */
    nothingCounted: 'Nothing has been counted yet. Type at least one counted quantity.',
    /** Not open (double-post, edit-after-post). */
    notOpen: 'This count is closed. Start a new count to correct it.',
    /** INV-05 §15 "two users at once" — the second poster is told. */
    alreadyPosted: (warehouseName) => `${warehouseName} has already been counted and posted. Refresh to see the result.`,
    /** GEN-59: the cancelling act carries its reason. */
    cancelReasonRequired: 'A count can only be cancelled with a reason, which is stored on the count and written to the audit log.',
};
/**
 * INV-05 §5.7's severity colour: |variance| 0 green, 1..tolerance amber,
 * above red. Since PART 15 landed the tolerance is the COMPANY SETTING
 * `inventory.count_variance_tolerance` (OQ-16-05); this is its registry
 * default, kept as the fallback while the effective settings load.
 */
export const COUNT_VARIANCE_TOLERANCE_DEFAULT = SETTINGS_REGISTRY['inventory.count_variance_tolerance'].default;
// ── Shapes ──────────────────────────────────────────────────────────────────
export const StockCountLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    itemId: z.uuid(),
    itemCode: z.string(),
    itemName: z.string(),
    unitSymbol: z.string(),
    /**
     * `N-4` — "the number of decimals the item's unit of measure allows". The
     * symbol was already here and the decimals were not, so the screen could
     * write "kg" beside a figure it had no right to round. A count line is one
     * ITEM at its stock unit (the snapshot comes from `stock_levels`), so this
     * is `units.precision` for that stock unit and there is no line unit to
     * disagree with it.
     */
    unitPrecision: UnitPrecisionSchema,
    /** The book figure (snapshot; re-read at posting). */
    systemQty: z.string(),
    /** NULL = not counted (BR-INV-05-13). */
    countedQty: z.string().nullable(),
    /** system − counted; null while not counted. POSITIVE = MISSING. */
    variance: z.string().nullable(),
    /**
     * MD-03 panel 7, carried onto the count row because the grid has to know
     * what to ask for: a serial-tracked row wants one identifier per counted
     * unit, a batch-tracked row wants the lot.
     */
    trackingType: z.enum(['none', 'serial', 'batch']),
    /** The serials found on the shelf — one per counted unit. */
    serialNos: z.string().array().nullable(),
    /** The lot this row's difference belongs to. */
    batchNo: z.string().nullable(),
})
    .strict();
export const StockCountSchema = z
    .object({
    id: z.uuid(),
    /** STK-####. */
    code: z.string(),
    countNo: z.number().int(),
    countDate: z.string(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    status: z.enum(['open', 'posted', 'cancelled']),
    postedAt: z.string().nullable(),
    /** GEN-59: why the count was cancelled — null until it is, and on rows cancelled before the reason existed. */
    cancelReason: z.string().nullable(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    /** Lines counted (not blank) / lines with a variance ≠ 0. */
    countedLines: z.number().int(),
    discrepancies: z.number().int(),
    createdByName: z.string().nullable(),
    lines: StockCountLineSchema.array(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
/** A counted shelf is never negative; 3 decimals like every quantity. */
const CountedQtySchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,3})?$/, 'Counted quantities are decimal strings with at most 3 decimals');
export const CreateStockCountRequestSchema = z
    .object({
    warehouseId: z.uuid(),
    countDate: z.iso.date().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
export const UpdateStockCountRequestSchema = z
    .object({
    notes: z.string().max(2000).nullable().optional(),
    /** Replace-set of entered counts. Omitted items stay "not counted". */
    counts: z
        .object({
        itemId: z.uuid(),
        /** null clears the entry back to "not counted". */
        countedQty: CountedQtySchema.nullable(),
        /**
         * BR-MD-03-17 on a count: WHICH units are present. The counted
         * quantity stays field 29's own number — these say what makes it up,
         * so the stock_take movement a difference posts can name the unit
         * that arrived or left. Typed freely while the count is open; counted
         * at posting, like every other document.
         */
        serialNos: z.string().trim().min(1).max(60).array().max(1000).nullable().optional(),
        batchNo: z.string().trim().min(1).max(40).nullable().optional(),
    })
        .strict()
        .array()
        .max(2000)
        .optional(),
})
    .strict();
const countParams = z.object({ countId: z.uuid() }).strict();
export const stockCountsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/stock-counts',
        query: pagedQuery({
            warehouseId: z.uuid().optional(),
            status: z.enum(['open', 'posted', 'cancelled']).optional(),
        }),
        responses: {
            200: pagedResponse('counts', StockCountSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Stock takes (inventory.view), newest first — the history list: date, warehouse, who counted, discrepancies. PAGED (NFR-42), both filters in SQL.',
    },
    get: {
        method: 'GET',
        path: '/api/stock-counts/:countId',
        pathParams: countParams,
        responses: {
            200: StockCountSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One count with its lines (inventory.view). Inactive items are included (INV-05 §5.7).',
    },
    create: {
        method: 'POST',
        path: '/api/stock-counts',
        body: CreateStockCountRequestSchema,
        responses: {
            201: StockCountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Open a count (inventory.create): snapshots one line per item stocked at the location, inactive included, blank = not counted.',
    },
    update: {
        method: 'PATCH',
        path: '/api/stock-counts/:countId',
        pathParams: countParams,
        body: UpdateStockCountRequestSchema,
        responses: {
            200: StockCountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Type counts into an OPEN count (inventory.edit; BR-INV-05-18 — freely editable, nothing moves).',
    },
    post: {
        method: 'POST',
        path: '/api/stock-counts/:countId/post',
        pathParams: countParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: StockCountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post (inventory.approve): one Stock Take movement per discrepant line (BR-INV-05-15), variance = system − counted (BR-INV-05-12), real user and time (BR-INV-05-16), the INV-05 §12 entry. One transaction.',
    },
    cancel: {
        method: 'POST',
        path: '/api/stock-counts/:countId/cancel',
        pathParams: countParams,
        /** GEN-59: never empty — the reason is the act's price of admission. */
        body: z
            .object({
            reason: z.string().trim().min(1, COUNT_MESSAGES.cancelReasonRequired).max(500),
        })
            .strict(),
        responses: {
            200: StockCountSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Abandon an OPEN count (inventory.edit). Nothing has moved, nothing will. GEN-59: reason required, stored on the count and written to the audit row.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=stock-counts.js.map